import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { patientAnamnesisApi } from '@/api/patient-anamnesis';
import type { SaveInlineAnamnesisPayload } from '@/types/patient-anamnesis';

export const getPatientAnamnesisQueryKey = (patientId?: string) =>
    ['patients', patientId, 'anamnesis'] as const;

export const usePatientAnamnesis = (patientId?: string) => {
    return useQuery({
        queryKey: getPatientAnamnesisQueryKey(patientId),
        queryFn: () => patientAnamnesisApi.get(patientId || ''),
        enabled: Boolean(patientId),
    });
};

export const useSaveAnamnesis = (patientId?: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (payload: SaveInlineAnamnesisPayload) =>
            patientAnamnesisApi.saveInline(patientId || '', payload),
        onSuccess: (data) => {
            queryClient.setQueryData(getPatientAnamnesisQueryKey(patientId), data);
        },
    });
};

export const useUploadAnamnesis = (patientId?: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (file: File) => patientAnamnesisApi.uploadFile(patientId || '', file),
        onSuccess: (data) => {
            queryClient.setQueryData(getPatientAnamnesisQueryKey(patientId), data);
        },
    });
};

export const useDeleteAnamnesis = (patientId?: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: () => patientAnamnesisApi.remove(patientId || ''),
        onSuccess: () => {
            // Reset to the "no anamnesis" state so the tab and hero pill update.
            queryClient.invalidateQueries({ queryKey: getPatientAnamnesisQueryKey(patientId) });
        },
    });
};
